import { createContext, type FC, type ReactNode } from "react";

import type { CheckoutConfiguration } from "@commerce/types/CheckoutConfiguration.types";

export const CheckoutConfigurationContext = createContext<CheckoutConfiguration>({
  payOnDelivery: {
    paymentMethod: "",
  },
  zeroValueOrder: {
    userNotification: "",
  },
  loyalty: {
    learnMoreUrl: "",
    orderConfirmationLoyaltyUrl: "",
    redemptionTabSymbol: "",
    showMgm: false,
    showReminderDialog: false,
    reminderDialogTitle: "",
    reminderDialogSubtitle: "",
    reminderDialogText: "",
    reminderDialogSecondaryButtonText: "",
    reminderDialogSecondaryButtonLink: "",
    reminderDialogSKUs: "",
  },
  failedPaymentFlow: {
    title: "",
    message: "",
    retryPaymentButtonLabel: "",
  },
  contactConfig: {
    differentRecipientDisabled: false,
    populatedPhoneInput: false,
  },
  discountDisclaimer: {
    enabled: false,
    enabledWhenZeroValueDeposit: false,
    disclaimerMessage: "",
    warningMessage: "",
  },
  ageVerificationPopup: {
    description: "",
    successMessage: "",
    popupMode: false,
  },
  ageVerificationMethodImages: {
    international_passport: "",
    passport: "",
    driving_license: "",
  },
  taxInfo: undefined,
  deliveryOptions: [],
  orderSummaryConfig: {
    displayItemsSummary: true,
    displayShippingAddress: true,
    displayDeliveryMethod: true,
    displayBillingAddress: true,
    displayPaymentMethod: true,
    displayLoyaltyConfirmation: true,
    displayAdditionalInfo: true,
    additionalInfo: {
      title: "",
      message: "",
    },
    displaySocialMedia: true,
    socialMediaTitle: "Follow us on",
    socialMedia: [],
    withdrawOrderInfo: "",
  },
  consentReminderPopup: {
    consentReminderPopupEnabled: false,
    maximumOccurancesPerYear: 0,
    consentExpirationLimit: 0,
    popUpDelay: 0,
    offerConsentName: "",
    productInformationConsentName: "",
    ploomClubConsentName: "",
    mainImage: "",
    imageAltText: "",
    subtitle: "",
    mainQuestion: "",
    description: "",
    yesButtonText: "",
    noButtonText: "",
    yesRedirectUrl: "",
  },
});

type CheckoutConfigurationData = Omit<CheckoutConfiguration, "deliveryOptions"> & {
  deliveryOptions?: CheckoutConfiguration["deliveryOptions"] | null;
};

type CheckoutConfigurationProviderProps = {
  children: ReactNode;
  data: CheckoutConfigurationData;
};

export const CheckoutConfigurationProvider: FC<CheckoutConfigurationProviderProps> = ({ children, data }) => {
  const value = {
    ...data,
    deliveryOptions: data.deliveryOptions || [],
  };
  return <CheckoutConfigurationContext.Provider value={value}>{children}</CheckoutConfigurationContext.Provider>;
};
